import {businessCalendarDate} from 'src/api/business-time'

import {
    allocateResourcesToJob,
    applyDriverNotification,
    commitJobToTimeline,
    isJobDispatched,
    overlappingConflictJobIds,
    replaceAllocationsForJob,
} from '../dispatch-timeline/allocation'
import {preserveRequirementOrder} from '../dispatch-timeline/requirement-order'
import type {
    BoardJobIdentity,
    DispatchTimelineData,
    JobAllocation,
    RequirementKind,
} from '../dispatch-timeline/types'
import {type JobDataLayer, jobDataLayer} from '../jobs/job-data-layer'
import {parseApiDate} from '../jobs/job-schedule-format'
import {
    applyStockEquipmentAssignmentDelta,
    applyStockEquipmentReassignment,
} from '../jobs/stock-equipment/api'
import type {
    ApplyStockEquipmentAssignmentDeltaInput,
    ApplyStockEquipmentReassignmentInput,
} from '../jobs/stock-equipment/schemas'
import {getDispatchBoard, getDispatchBoardJob, markDispatchWorkerAssignmentNotified} from './api'
import {
    boardJobIdentity,
    mapBoardJobToUnassignedJob,
    mapBoardResponseToDispatchTimelineData,
} from './board-adapter'
import {allocationsToAssignmentValues, extractAllocationsFromBoardState} from './dispatch-mapper'

type AllocateResourceInput = {
    requirementId: string
    resourceId: string
    resourceName: string
    kind: RequirementKind
    days?: JobAllocation['days']
}

export type StockDeltaInput = ApplyStockEquipmentAssignmentDeltaInput
export type StockReassignmentInput = ApplyStockEquipmentReassignmentInput

export type DispatchDataLayer = {
    loadBoard: (startDate: Date | string, endDate?: Date | string) => Promise<DispatchTimelineData>
    allocateResource: (
        data: DispatchTimelineData,
        jobId: string,
        input: AllocateResourceInput,
    ) => Promise<DispatchTimelineData>
    allocateResources: (
        data: DispatchTimelineData,
        jobId: string,
        inputs: AllocateResourceInput[],
    ) => Promise<DispatchTimelineData>
    applyStockDelta: (
        data: DispatchTimelineData,
        jobId: string,
        input: StockDeltaInput,
    ) => Promise<DispatchTimelineData>
    applyStockReassignment: (
        data: DispatchTimelineData,
        jobId: string,
        input: StockReassignmentInput,
        sourceJobIds: string[],
    ) => Promise<DispatchTimelineData>
    replaceAllocations: (
        data: DispatchTimelineData,
        jobId: string,
        allocations: JobAllocation[],
    ) => Promise<DispatchTimelineData>
    notifyDriver: (
        data: DispatchTimelineData,
        jobId: string,
        resourceId: string,
    ) => Promise<DispatchTimelineData>
}

function replaceBoardJob(
    data: DispatchTimelineData,
    jobId: string,
    boardJob: ReturnType<typeof mapBoardJobToUnassignedJob>,
    identity: BoardJobIdentity,
): DispatchTimelineData {
    const prior = data.unassignedJobs.find((job) => job.id === jobId)
    const mergedJob = {
        ...boardJob,
        allocations: boardJob.allocations,
        requirements: preserveRequirementOrder(prior?.requirements, boardJob.requirements),
    }

    const unassignedJobs = data.unassignedJobs.filter((job) => job.id !== jobId)
    const assignments = data.assignments.filter(
        (assignment) => assignment.sourceJobId !== jobId && assignment.id !== jobId,
    )
    if (isJobDispatched(mergedJob)) {
        assignments.push(commitJobToTimeline(mergedJob))
    } else {
        unassignedJobs.push(mergedJob)
    }

    return {
        ...data,
        unassignedJobs,
        assignments,
        jobIdentities: {...data.jobIdentities, [jobId]: identity},
    }
}

function anchorDateForBoard(data: DispatchTimelineData): Date {
    return data.scheduleWindowStart != null
        ? parseApiDate(data.scheduleWindowStart)
        : businessCalendarDate()
}

function createDispatchDataLayer(jobs: JobDataLayer): DispatchDataLayer {
    async function allocateResources(
        data: DispatchTimelineData,
        jobId: string,
        inputs: AllocateResourceInput[],
    ): Promise<DispatchTimelineData> {
        if (inputs.length === 0) {
            return data
        }

        const targetJob = data.unassignedJobs.find((job) => job.id === jobId)
        const conflictJobIds = new Set<string>()
        if (targetJob != null) {
            for (const input of inputs.filter((entry) => entry.kind !== 'worker')) {
                for (const conflictJobId of overlappingConflictJobIds(
                    input.resourceId,
                    targetJob,
                    data,
                )) {
                    conflictJobIds.add(conflictJobId)
                }
            }
        }

        const next = allocateResourcesToJob(data, jobId, inputs)

        // Persist the target job before unassigning conflicting jobs.
        await jobs.saveAssignments(
            jobId,
            allocationsToAssignmentValues(extractAllocationsFromBoardState(next, jobId)),
        )

        for (const conflictJobId of conflictJobIds) {
            await jobs.saveAssignments(
                conflictJobId,
                allocationsToAssignmentValues(
                    extractAllocationsFromBoardState(next, conflictJobId),
                ),
            )
        }

        let result = next
        const anchor = anchorDateForBoard(data)
        for (const conflictJobId of conflictJobIds) {
            const conflictBoardJob = await getDispatchBoardJob(conflictJobId)
            result = replaceBoardJob(
                result,
                conflictJobId,
                mapBoardJobToUnassignedJob(conflictBoardJob, anchor),
                boardJobIdentity(conflictBoardJob),
            )
        }

        const boardJob = await getDispatchBoardJob(jobId)
        return replaceBoardJob(
            result,
            jobId,
            mapBoardJobToUnassignedJob(boardJob, anchor),
            boardJobIdentity(boardJob),
        )
    }

    return {
        async loadBoard(startDate, endDate) {
            const response = await getDispatchBoard({startDate, endDate})
            return mapBoardResponseToDispatchTimelineData(response)
        },

        allocateResources,

        async applyStockDelta(data, jobId, input) {
            await applyStockEquipmentAssignmentDelta(jobId, input)
            const boardJob = await getDispatchBoardJob(jobId)
            const updatedJob = mapBoardJobToUnassignedJob(boardJob, anchorDateForBoard(data))
            return replaceBoardJob(data, jobId, updatedJob, boardJobIdentity(boardJob))
        },

        async applyStockReassignment(data, jobId, input, sourceJobIds) {
            await applyStockEquipmentReassignment(jobId, input)
            const anchor = anchorDateForBoard(data)
            let result = data
            const uniqueSourceIds = [...new Set(sourceJobIds.filter((id) => id !== jobId))]
            for (const sourceJobId of uniqueSourceIds) {
                const sourceBoardJob = await getDispatchBoardJob(sourceJobId)
                result = replaceBoardJob(
                    result,
                    sourceJobId,
                    mapBoardJobToUnassignedJob(sourceBoardJob, anchor),
                    boardJobIdentity(sourceBoardJob),
                )
            }
            const boardJob = await getDispatchBoardJob(jobId)
            return replaceBoardJob(
                result,
                jobId,
                mapBoardJobToUnassignedJob(boardJob, anchor),
                boardJobIdentity(boardJob),
            )
        },

        async replaceAllocations(data, jobId, allocations) {
            const next = replaceAllocationsForJob(data, jobId, allocations)
            await jobs.saveAssignments(jobId, allocationsToAssignmentValues(allocations))
            const boardJob = await getDispatchBoardJob(jobId)
            const updatedJob = mapBoardJobToUnassignedJob(boardJob, anchorDateForBoard(data))
            return replaceBoardJob(next, jobId, updatedJob, boardJobIdentity(boardJob))
        },

        async allocateResource(data, jobId, input) {
            return allocateResources(data, jobId, [input])
        },

        async notifyDriver(data, jobId, resourceId) {
            const job = data.unassignedJobs.find((entry) => entry.id === jobId)
            const allocation = job?.allocations.find(
                (entry) => entry.kind === 'worker' && entry.resourceId === resourceId,
            )
            if (allocation?.assignmentId == null) {
                throw new Error('Worker assignment is missing its backend ID')
            }
            const updatedBoardJob = await markDispatchWorkerAssignmentNotified(
                jobId,
                allocation.assignmentId,
            )
            return replaceBoardJob(
                data,
                jobId,
                mapBoardJobToUnassignedJob(updatedBoardJob, anchorDateForBoard(data)),
                boardJobIdentity(updatedBoardJob),
            )
        },
    }
}

export const dispatchDataLayer = createDispatchDataLayer(jobDataLayer)

export function createTestDispatchDataLayer(
    initialBoard: DispatchTimelineData,
    getNotificationTimestamp: () => number,
): DispatchDataLayer {
    let board = structuredClone(initialBoard)

    return {
        async loadBoard() {
            return structuredClone(board)
        },

        async allocateResource(data, jobId, input) {
            board = allocateResourcesToJob(data, jobId, [input])
            return structuredClone(board)
        },

        async allocateResources(data, jobId, inputs) {
            board = allocateResourcesToJob(data, jobId, inputs)
            return structuredClone(board)
        },

        async applyStockDelta(data, jobId, _input) {
            void jobId
            void _input
            return structuredClone(data)
        },

        async applyStockReassignment(data, jobId, _input, _sourceJobIds) {
            void jobId
            void _input
            void _sourceJobIds
            return structuredClone(data)
        },

        async replaceAllocations(data, jobId, allocations) {
            board = replaceAllocationsForJob(data, jobId, allocations)
            return structuredClone(board)
        },

        async notifyDriver(data, jobId, resourceId) {
            board = applyDriverNotification(data, jobId, resourceId, getNotificationTimestamp())
            return structuredClone(board)
        },
    }
}

export function createTestDispatchDataLayerFromLayers(jobs: JobDataLayer): DispatchDataLayer {
    return createDispatchDataLayer(jobs)
}
